import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { ATS_PIPELINE_STAGES, errorWords } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { optionalString, KEEP_PLACE } from "@/lib/search";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useEveryApplication } from "@/routes/_authenticated/_workforce/hiring/-components/use-every-application";
import { advanceGate } from "./-components/advance-gates";
import { CandidateChips } from "./-components/candidate-toolbar";
import { CandidateFilters } from "@/routes/_authenticated/_workforce/hiring/applications/-components/candidate-filters";
import { RejectDialog } from "./-components/decision-dialogs";
import { useCan } from "@/lib/policy";
import {
  applicantName,
  ApplicationsTable,
  type ApplicationRow,
  type QuickAction,
} from "./-components/applications-table";

const ALL = "all";
const STAGE_OPTIONS = [...ATS_PIPELINE_STAGES, "rejected"];

const isStage = (value: unknown): value is string =>
  typeof value === "string" && STAGE_OPTIONS.includes(value);

export const Route = createFileRoute("/_authenticated/_workforce/hiring/applications/")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { stage?: string; job?: string; status?: string } => ({
    stage: isStage(search.stage) ? search.stage : undefined,
    job: optionalString(search.job) || undefined,
    status: optionalString(search.status) || undefined,
  }),
  component: Applications,
});

/** Everyone in the hiring pipeline, with the three decisions you can take without opening a
 * record. */
function Applications() {
  const { facilityId } = useFacility();
  const { stage, job, status } = Route.useSearch();
  const jobFilter = job ?? ALL;
  const statusFilter = status ?? ALL;
  const navigate = Route.useNavigate();
  const canManage = useCan("hiring:manage");
  const queryClient = useQueryClient();

  const refine = (next: { stage?: string; job?: string; status?: string }) =>
    void navigate({
      search: (prev) => ({ ...prev, ...next }),
      ...KEEP_PLACE,
    });
  const setJobFilter = (value: string) => refine({ job: value === ALL ? undefined : value });
  const setStatusFilter = (value: string) => refine({ status: value === ALL ? undefined : value });
  const [busyId, setBusyId] = useState<string | null>(null);
  /* Rejecting closes the application, so it asks for a reason first. */
  const [rejecting, setRejecting] = useState<ApplicationRow | null>(null);

  const jobs = useQuery(orpc.workforce.hiring.listJobs.queryOptions({ input: { facilityId } }));
  const applications = useEveryApplication({
    facilityId,
    jobId: jobFilter === ALL ? undefined : jobFilter,
    stage,
  });

  const settled = {
    onSettled: () => setBusyId(null),
    /* A paperwork gate is answerable, and the place to answer it is the
       candidate's own record, where the confirmation dialog lives. Anything
       else is a plain refusal and reads as one. */
    onError: (error: Error) =>
      toast.error(
        advanceGate(error)
          ? `${errorWords(error)} Open the candidate to confirm.`
          : `${errorWords(error)} Open the candidate to see why.`,
      ),
  };
  const done = async (message: string) => {
    toast.success(message);
    await queryClient.invalidateQueries({
      queryKey: orpc.workforce.hiring.key(),
    });
  };

  const advance = useMutation(
    orpc.workforce.hiring.advance.mutationOptions({
      ...settled,
      onSuccess: () => done("Moved forward"),
    }),
  );
  const hold = useMutation(
    orpc.workforce.hiring.setStatus.mutationOptions({
      ...settled,
      onSuccess: () => done("Put on hold"),
    }),
  );
  const resume = useMutation(
    orpc.workforce.hiring.setStatus.mutationOptions({
      ...settled,
      onSuccess: () => done("Back in the pipeline"),
    }),
  );
  const reject = useMutation(
    orpc.workforce.hiring.reject.mutationOptions({
      ...settled,
      onSuccess: () => {
        setRejecting(null);
        return done("Candidate rejected");
      },
    }),
  );

  const quickAction = (application: ApplicationRow, action: QuickAction) => {
    if (action === "reject") {
      setRejecting(application);
      return;
    }
    setBusyId(application.id);
    if (action === "advance") advance.mutate({ applicationId: application.id });
    else if (action === "hold") hold.mutate({ applicationId: application.id, status: "hold" });
    else resume.mutate({ applicationId: application.id, status: "in_progress" });
  };

  const setStage = (value: string) => refine({ stage: value === ALL ? undefined : value });

  // The list arrives whole, so narrowing it by status here costs nothing and a round trip would.
  const all = applications.data?.rows ?? [];
  const shown = all.filter(
    (application) => statusFilter === ALL || application.stageStatus === statusFilter,
  );
  const statusCounts = applications.data
    ? all.reduce(
        (counts, row) => counts.set(row.stageStatus, (counts.get(row.stageStatus) ?? 0) + 1),
        new Map<string, number>(),
      )
    : undefined;

  const filtered = jobFilter !== ALL || statusFilter !== ALL || stage !== undefined;
  const clearFilters = () => {
    void navigate({ search: {}, ...KEEP_PLACE });
  };

  return (
    <>
      <PageHeader
        title="Candidates"
        count={applications.isPending ? undefined : shown.length}
        meta={
          applications.isPending
            ? undefined
            : shown.length > 0
              ? `In the pipeline${filtered ? ", filtered" : ""}`
              : "Move applicants through your hiring pipeline stage by stage."
        }
      />

      {applications.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : applications.isError ? (
        <ReadFailed
          message="Could not load candidates."
          error={applications.error}
          retrying={applications.isFetching}
          onRetry={() => void applications.refetch()}
        />
      ) : all.length === 0 && !filtered ? (
        <EmptyState
          title="No candidates yet"
          description="Applications appear here as candidates apply."
        />
      ) : (
        <ApplicationsTable
          applications={shown}
          empty={
            <EmptyState
              title={filtered ? "No candidates match these filters" : "No candidates yet"}
              description={
                filtered
                  ? "Widen the filters to see more of the pipeline."
                  : "Applications appear here as candidates apply."
              }
              action={
                filtered ? (
                  <Button variant="outline" onClick={clearFilters}>
                    Clear filters
                  </Button>
                ) : undefined
              }
            />
          }
          chips={
            <CandidateChips
              status={statusFilter}
              onStatus={setStatusFilter}
              counts={statusCounts}
            />
          }
          trailing={
            <CandidateFilters
              job={jobFilter}
              onJob={setJobFilter}
              jobs={jobs.data ?? []}
              stage={stage}
              onStage={setStage}
              stages={STAGE_OPTIONS}
            />
          }
          canManage={canManage}
          busyId={busyId}
          onQuickAction={quickAction}
        />
      )}

      <RejectDialog
        key={rejecting?.id}
        name={rejecting ? applicantName(rejecting) : ""}
        open={rejecting !== null}
        onOpenChange={(open) => (open ? null : setRejecting(null))}
        pending={reject.isPending}
        onSubmit={(reason) => {
          if (!rejecting) return;
          setBusyId(rejecting.id);
          reject.mutate({ applicationId: rejecting.id, reason });
        }}
      />
    </>
  );
}
